import { FormEvent, ReactNode, useState } from 'react'
import { HiOutlineChevronDown } from 'react-icons/hi2'

export type UserRole = 'manager' | 'sales'

export type UserFormValues = {
  name: string
  email: string
  phone: string
  role: UserRole
}

type Props = {
  initialValues: UserFormValues
  errors: Record<string, string[] | undefined>
  submitLabel: string
  cancelHref: string
  onSubmit: (values: UserFormValues) => void
  processing?: boolean
  roleDisabled?: boolean
  /** Optional extra action rendered on the left of the action row (e.g. Delete) */
  leftAction?: ReactNode
}

const ROLE_OPTIONS: Array<{ value: UserRole; label: string }> = [
  { value: 'sales', label: 'Sales' },
  { value: 'manager', label: 'Manager' },
]

export default function UserForm({
  initialValues,
  errors,
  submitLabel,
  cancelHref,
  onSubmit,
  processing = false,
  roleDisabled = false,
  leftAction,
}: Props) {
  const [values, setValues] = useState<UserFormValues>(initialValues)

  const update = <K extends keyof UserFormValues>(key: K, val: UserFormValues[K]) => {
    setValues((prev) => ({ ...prev, [key]: val }))
  }

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    onSubmit(values)
  }

  return (
    <form onSubmit={handleSubmit} className="mx-auto w-full max-w-[903px]">
      <div className="rounded-xl bg-white shadow-[0_1px_3px_0_rgba(0,0,0,0.1),0_1px_2px_0_rgba(0,0,0,0.06)]">
        <div className="flex items-center gap-4 px-6 pt-5">
          <h2 className="font-display text-lg font-medium text-ink">Personal Details</h2>
        </div>
        <div className="grid grid-cols-1 gap-4 p-6 pt-4 md:grid-cols-2">
          <Field label="Name" error={errors.name} className="md:col-span-2">
            <input
              type="text"
              value={values.name}
              onChange={(e) => update('name', e.target.value)}
              className={inputClass(errors.name)}
            />
          </Field>

          <Field label="Email" error={errors.email}>
            <input
              type="email"
              value={values.email}
              onChange={(e) => update('email', e.target.value)}
              className={inputClass(errors.email)}
            />
          </Field>

          <Field label="Phone" error={errors.phone}>
            <input
              type="tel"
              value={values.phone}
              onChange={(e) => update('phone', e.target.value)}
              className={inputClass(errors.phone)}
            />
          </Field>

          <Field label="Role" error={errors.role}>
            <div className="relative">
              <select
                value={values.role}
                onChange={(e) => update('role', e.target.value as UserRole)}
                disabled={roleDisabled}
                className={`${inputClass(errors.role)} appearance-none pr-10 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-black/40`}
              >
                {ROLE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <HiOutlineChevronDown
                className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500"
                aria-hidden="true"
              />
            </div>
            {roleDisabled && (
              <p className="mt-1 font-display text-xs text-black/40">
                You cannot change your own role.
              </p>
            )}
          </Field>
        </div>
      </div>

      <div className="mt-6 flex items-center justify-between gap-2">
        <a
          href={cancelHref}
          className="rounded-md border border-gray-300 bg-white px-5 py-3 font-display text-base font-medium text-ink transition hover:bg-gray-50"
        >
          Cancel
        </a>
        <div className="flex items-center gap-2">
          {leftAction}
          <button
            type="submit"
            disabled={processing}
            className="rounded-md bg-ink px-7 py-3 font-display text-base font-medium text-white shadow-sm transition hover:bg-midnight-green disabled:cursor-not-allowed disabled:opacity-60"
          >
            {submitLabel}
          </button>
        </div>
      </div>
    </form>
  )
}

function Field({
  label,
  error,
  children,
  className = '',
}: {
  label: string
  error?: string[]
  children: ReactNode
  className?: string
}) {
  return (
    <label className={`flex flex-col gap-1 ${className}`}>
      <span className="font-display text-base font-medium text-black/60">{label}</span>
      {children}
      {error && error.length > 0 && (
        <span className="mt-1 font-display text-xs text-rose-600">{error.join(', ')}</span>
      )}
    </label>
  )
}

function inputClass(error?: string[]) {
  const base =
    'w-full rounded-md border bg-white px-3 py-4 font-display text-base text-ink shadow-sm placeholder:text-black/40 focus:border-midnight-green focus:outline-none focus:ring-1 focus:ring-midnight-green'
  return `${base} ${error && error.length > 0 ? 'border-rose-400' : 'border-gray-300'}`
}
